import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { getStytchClient, logoutUser } from '@/lib/stytch'

type StytchOrganization = {
  organization_id: string
  organization_logo_url: string
  organization_name: string
}

type StytchMember = {
  member_id: string
  name: string
}

export const SESSION_EXPIRATION_MINUTES = 10080 // 7 days

export const useAuthStore = defineStore('auth', () => {
  const isAuthenticated = ref(false)
  const organization = ref<StytchOrganization | null>(null)
  const member = ref<StytchMember | null>(null)
  const stytchToken = ref<string | null>(null)
  const isAdmin = ref(false)
  const sessionRefreshInterval = ref<NodeJS.Timeout | null>(null)
  const sessionRefreshCount = ref(0)
  const MAX_SESSION_REFRESH_COUNT = 4

  function setSession(
    sessionToken: string,
    admin: boolean,
    stytchOrganization: StytchOrganization,
    stytchMember: StytchMember,
  ) {
    isAuthenticated.value = Boolean(sessionToken)
    stytchToken.value = sessionToken
    isAdmin.value = admin
    // only pick the fields we need
    organization.value = {
      organization_id: stytchOrganization.organization_id,
      organization_logo_url: stytchOrganization.organization_logo_url,
      organization_name: stytchOrganization.organization_name,
    }
    member.value = {
      member_id: stytchMember.member_id,
      name: stytchMember.name,
    }

    // refresh the session every SESSION_EXPIRATION_MINUTES - 10 minutes
    sessionRefreshInterval.value = setInterval(
      refreshStytchSession,
      (SESSION_EXPIRATION_MINUTES - 10) * 60 * 1000,
    )
    // cleanup
    window.addEventListener('beforeunload', () => {
      if (sessionRefreshInterval.value) clearInterval(sessionRefreshInterval.value)
    })
  }

  function clearSession() {
    isAuthenticated.value = false
    organization.value = null
    member.value = null
    stytchToken.value = null
    isAdmin.value = false
    logoutUser()
  }

  function refreshStytchSession() {
    const stytch = getStytchClient()
    if (stytch.session.getSync()) {
      sessionRefreshCount.value++

      // after MAX_SESSION_REFRESH_COUNT refreshes, clear the session and log the user out
      if (sessionRefreshCount.value > MAX_SESSION_REFRESH_COUNT && sessionRefreshInterval.value) {
        clearInterval(sessionRefreshInterval.value)
        sessionRefreshInterval.value = null
        clearSession()
        return
      }

      stytch.session.authenticate({
        session_duration_minutes: SESSION_EXPIRATION_MINUTES,
      })
    }
  }

  const isLoggedIn = computed(() => isAuthenticated.value)

  return {
    isAuthenticated,
    isLoggedIn,
    stytchToken,
    setSession,
    clearSession,
    isAdmin,
    organization,
    member,
  }
})
